import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { Link } from "react-router-dom";

// Building blocks for the redesigned course pages. The rest of the app still
// uses components/ui.tsx; these follow the admin design (deep-teal brand,
// solid table headers, card rows) and live here so the redesign stays
// scoped to the course pages.

type Variant = "primary" | "outline" | "soft";
const VARIANTS: Record<Variant, string> = {
  primary: "bg-[#0B5785] text-white hover:bg-[#0a4a71] shadow-sm",
  outline: "bg-white text-[#0B5785] ring-1 ring-inset ring-[#0B5785]/40 hover:bg-[#0B5785]/5",
  soft: "bg-[#F1F5F8] text-[#0B5785] hover:bg-[#e5edf3]",
};
const SIZES = {
  md: "min-h-[48px] px-5 text-[15px]",
  sm: "px-2.5 py-1 text-xs",
};

export function BrandButton({ variant = "outline", size = "md", loading, children, className = "", ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: keyof typeof SIZES;
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B5785]/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    >
      {loading ? "Working…" : children}
    </button>
  );
}

export function BrandLinkButton({ to, children, className = "" }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-[48px] items-center justify-center rounded-md bg-white px-5 text-[15px] font-medium text-[#0B5785] ring-1 ring-inset ring-[#0B5785]/40 transition-colors hover:bg-[#0B5785]/5 ${className}`}
    >
      {children}
    </Link>
  );
}

export function BackButton({ to, label = "Back" }: { to: string; label?: string }) {
  return (
    <Link
      to={to}
      aria-label={label}
      title={label}
      className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E8EEF3] text-[#0B5785] transition-colors hover:bg-[#d8e3ec]"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path d="M19 12H6M11 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Link>
  );
}

export function CoursePageHeader({ back, title, subtitle, actions }: {
  back?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-10">
        {back && <BackButton to={back} />}
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-5">{actions}</div>}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search ...", className = "" }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <label className={`relative block ${className}`}>
      <span className="sr-only">{placeholder}</span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-12 w-full rounded-lg border border-gray-200 bg-white pl-5 pr-12 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0B5785] focus:outline-none focus:ring-2 focus:ring-[#0B5785]/20"
      />
      <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
      </svg>
    </label>
  );
}

export function SelectInput({ children, className = "", ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={`relative ${className}`}>
      <select
        {...rest}
        className="h-12 w-full appearance-none rounded-md border border-gray-200 bg-white pl-3.5 pr-10 text-sm font-medium text-gray-900 focus:border-[#0B5785] focus:outline-none focus:ring-2 focus:ring-[#0B5785]/20"
      >
        {children}
      </select>
      <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-700" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function StatTile({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: "danger" | "warning" }) {
  const valueTone = tone === "danger" ? "text-red-600" : tone === "warning" ? "text-amber-700" : "text-gray-900";
  return (
    <div className="flex min-h-[120px] flex-col items-center justify-center rounded-lg border border-gray-100 bg-white px-3 py-4 text-center shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
      <div className="text-[17px] font-medium text-[#0B5785]">{label}</div>
      <div className={`mt-2 text-[32px] font-semibold leading-none tabular-nums ${valueTone}`}>{value}</div>
      {hint && <div className="mt-2 text-sm leading-snug text-gray-600">{hint}</div>}
    </div>
  );
}

// A table whose header is a solid brand bar and whose rows sit flush on the
// page background, separated only by hairline dividers.
export function DataTable({ head, children, minWidth = 900 }: { head: ReactNode; children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0" style={{ minWidth }}>
        <thead>
          <tr className="text-white [&>th:first-child]:rounded-l-lg [&>th:last-child]:rounded-r-lg">{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function HeadCell({ children, className = "", title }: { children?: ReactNode; className?: string; title?: string }) {
  return (
    <th title={title} className={`bg-[#0B5785] px-4 py-4 shadow-[1px_0_0_#0B5785] text-left text-[15px] font-medium first:pl-7 last:pr-7 ${className}`}>
      {children}
    </th>
  );
}

export function RowCell({ children, className = "", colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return (
    <td colSpan={colSpan} className={`border-b border-gray-200 px-4 py-3 text-sm text-gray-900 first:pl-7 last:pr-7 ${className}`}>
      {children}
    </td>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="rounded-lg border border-dashed border-gray-200 bg-white py-10 text-center text-sm text-gray-500">{children}</p>;
}

// Back · 1 2 3 … · Next. `page` is zero-based; `pages` may be unknown when
// the API gives no total, in which case only Back/Next are driven by hasNext.
export function NumberPager({ page, pages, hasNext, onChange }: {
  page: number;
  pages: number | undefined;
  hasNext: boolean;
  onChange: (page: number) => void;
}) {
  if (page === 0 && !hasNext) return null;
  const nums = pages ? pageWindow(page, pages) : [page];
  const base = "inline-flex h-9 items-center justify-center rounded-md border text-sm transition-colors";
  return (
    <nav aria-label="Pagination" className="mt-8 flex justify-end gap-2">
      <button
        className={`${base} gap-1 border-gray-200 bg-white px-3 text-[#0B5785] hover:bg-gray-50 disabled:opacity-40`}
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        <span aria-hidden>‹</span> Back
      </button>
      {nums.map((n, i) =>
        n === null ? (
          <span key={`gap${i}`} className="px-1 text-gray-400 leading-9">…</span>
        ) : (
          <button
            key={n}
            aria-current={n === page ? "page" : undefined}
            className={`${base} w-9 ${n === page ? "border-[#0B5785] bg-[#0B5785] font-semibold text-white" : "border-gray-200 bg-white text-[#0B5785] hover:bg-gray-50"}`}
            onClick={() => onChange(n)}
          >
            {n + 1}
          </button>
        )
      )}
      <button
        className={`${base} gap-1 border-gray-200 bg-white px-3 text-[#0B5785] hover:bg-gray-50 disabled:opacity-40`}
        disabled={!hasNext}
        onClick={() => onChange(page + 1)}
      >
        Next <span aria-hidden>›</span>
      </button>
    </nav>
  );
}

function pageWindow(page: number, pages: number): (number | null)[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i);
  const set = new Set([0, pages - 1, page - 1, page, page + 1].filter((n) => n >= 0 && n < pages));
  const sorted = [...set].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push(null);
    out.push(n);
  });
  return out;
}
